import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { getContent, getRawContent, listSpaces, getSpaceContent, createChatThread, streamChat } from '../lib/api'
import { getIdToken } from '../lib/firebase'
import { formatDate, contentTypeConfig } from '../lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ArrowLeft,
  ChevronRight,
  ChevronDown,
  MessageSquare,
  Edit,
  Trash2,
  Share2,
  ExternalLink,
  Loader2,
  FileText,
  Globe,
  Image as ImageIcon,
  Mic,
  BookOpen,
  File,
  Calendar,
  Clock,
  Download,
  Eye,
  Volume2,
  Play,
  Pause,
  RotateCcw,
  Info,
  Columns,
  Square,
  Maximize2,
  PanelLeftClose,
  PanelRightClose,
  FolderOpen,
  Folder,
  Home,
  RefreshCw,
  Send,
  X,
  Bot,
  User,
  Sparkles,
  Paperclip,
  Plus,
} from 'lucide-react'
import { AttachedContents, ContentPickerModal } from '../components/ContentPicker'

const iconMap = {
  note: FileText,
  bookmark: Globe,
  image: ImageIcon,
  pdf: FileText,
  docx: FileText,
  doc: FileText,
  xlsx: FileText,
  xls: FileText,
  pptx: FileText,
  ppt: FileText,
  txt: FileText,
  epub: BookOpen,
  transcript: Mic,
  unknown: File,
}

// View modes similar to mobile app
const ViewMode = {
  ORIGINAL: 'original',
  MARKDOWN: 'markdown',
  LISTEN: 'listen',
  INFO: 'info',
}

export default function ContentPage() {
  const { contentId } = useParams()
  const navigate = useNavigate()
  const [content, setContent] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewMode, setViewMode] = useState(ViewMode.ORIGINAL)
  const [rawContent, setRawContent] = useState(null)
  const [rawLoading, setRawLoading] = useState(false)
  
  // Layout state
  const [layoutMode, setLayoutMode] = useState('master-detail') // 'single' | 'master-detail' | 'focus'
  const [showSidebar, setShowSidebar] = useState(true)
  
  // Tree state for master-detail mode
  const [spaces, setSpaces] = useState([])
  const [rootContents, setRootContents] = useState([])
  const [expandedSpaces, setExpandedSpaces] = useState({})
  const [spaceContents, setSpaceContents] = useState({})
  const [treeLoading, setTreeLoading] = useState(false)
  
  // Chat drawer state
  const [showChat, setShowChat] = useState(false)
  const [chatThreadId, setChatThreadId] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [attachedContents, setAttachedContents] = useState([])
  const [showContentPicker, setShowContentPicker] = useState(false)

  useEffect(() => {
    loadContent()
  }, [contentId])

  // Load tree data when switching to master-detail mode
  useEffect(() => {
    if (layoutMode === 'master-detail' && spaces.length === 0) {
      loadTreeData()
    }
  }, [layoutMode])

  const loadTreeData = async () => {
    setTreeLoading(true)
    try {
      const data = await listSpaces()
      setSpaces(data.spaces || [])
      setRootContents(data.contents || [])
    } catch (err) {
      console.error('Failed to load tree:', err)
    } finally {
      setTreeLoading(false)
    }
  }

  const toggleSpace = async (spaceId) => {
    const isExpanded = expandedSpaces[spaceId]
    setExpandedSpaces(prev => ({ ...prev, [spaceId]: !isExpanded }))
    
    if (!isExpanded && !spaceContents[spaceId]) {
      try {
        const data = await getSpaceContent(spaceId)
        setSpaceContents(prev => ({ ...prev, [spaceId]: data.contents || [] }))
      } catch (err) {
        console.error('Failed to load space contents:', err)
      }
    }
  }

  // Chat functions
  const openChat = () => {
    setShowChat(true)
    // Don't create thread here - wait for first message
  }

  const sendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return
    
    const userMessage = chatInput.trim()
    setChatInput('')
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }])
    setChatLoading(true)

    try {
      // Create thread if not exists - include question in creation
      let threadId = chatThreadId
      if (!threadId) {
        // Include current content and any additional attached contents
        const allContentIds = [
          contentId,
          ...attachedContents.map(c => c.content_id || c.id).filter(id => id !== contentId)
        ]
        console.log('Creating new chat thread with question:', userMessage, 'contentIds:', allContentIds)
        const thread = await createChatThread({
          name: `Chat: ${content?.title || 'Content'}`,
          contentIds: allContentIds,
          question: userMessage, // Required by API
        })
        console.log('Thread created:', thread)
        threadId = thread.id || thread.threadId
        setChatThreadId(threadId)
        
        // Add empty assistant message placeholder
        setChatMessages(prev => [...prev, { role: 'assistant', content: '' }])
        
        // The createChatThread API streams the first response
        // Check if thread has a streaming response or direct response
        if (thread.response) {
          setChatMessages(prev => {
            const newMessages = [...prev]
            newMessages[newMessages.length - 1] = { role: 'assistant', content: thread.response }
            return newMessages
          })
        } else {
          // Stream from the newly created thread
          let assistantMessage = ''
          for await (const chunk of streamChat(threadId, userMessage)) {
            console.log('Stream chunk:', chunk)
            if (chunk.type === 'token' && chunk.token) {
              assistantMessage += chunk.token
              setChatMessages(prev => {
                const newMessages = [...prev]
                newMessages[newMessages.length - 1] = { role: 'assistant', content: assistantMessage }
                return newMessages
              })
            } else if (chunk.type === 'done' && chunk.response && !assistantMessage) {
              // Use final response if we didn't get tokens
              setChatMessages(prev => {
                const newMessages = [...prev]
                newMessages[newMessages.length - 1] = { role: 'assistant', content: chunk.response }
                return newMessages
              })
            } else if (chunk.type === 'error') {
              setChatMessages(prev => {
                const newMessages = [...prev]
                newMessages[newMessages.length - 1] = { role: 'assistant', content: `Error: ${chunk.error}` }
                return newMessages
              })
            }
          }
        }
      } else {
        // Stream the response for existing thread
        let assistantMessage = ''
        setChatMessages(prev => [...prev, { role: 'assistant', content: '' }])

        for await (const chunk of streamChat(threadId, userMessage)) {
          console.log('Stream chunk:', chunk)
          if (chunk.type === 'token' && chunk.token) {
            assistantMessage += chunk.token
            setChatMessages(prev => {
              const newMessages = [...prev]
              newMessages[newMessages.length - 1] = { role: 'assistant', content: assistantMessage }
              return newMessages
            })
          } else if (chunk.type === 'done' && chunk.response && !assistantMessage) {
            // Use final response if we didn't get tokens
            setChatMessages(prev => {
              const newMessages = [...prev]
              newMessages[newMessages.length - 1] = { role: 'assistant', content: chunk.response }
              return newMessages
            })
          } else if (chunk.type === 'error') {
            setChatMessages(prev => {
              const newMessages = [...prev]
              newMessages[newMessages.length - 1] = { role: 'assistant', content: `Error: ${chunk.error}` }
              return newMessages
            })
          }
        }
      }
    } catch (err) {
      console.error('Chat error:', err)
      setChatMessages(prev => {
        // Check if we already added an assistant message placeholder
        const lastMsg = prev[prev.length - 1]
        if (lastMsg?.role === 'assistant' && !lastMsg.content) {
          const newMessages = [...prev]
          newMessages[newMessages.length - 1] = { role: 'assistant', content: 'Sorry, an error occurred. Please try again.' }
          return newMessages
        }
        return [...prev, { role: 'assistant', content: 'Sorry, an error occurred. Please try again.' }]
      })
    } finally {
      setChatLoading(false)
    }
  }

  const handleContentSelect = (contents) => {
    // Filter out the current content since it's always attached
    setAttachedContents(contents.filter(c => (c.content_id || c.id) !== contentId))
  }

  const handleRemoveContent = (contentToRemove) => {
    const id = contentToRemove.content_id || contentToRemove.id
    if (id === contentId) return // Can't remove the main content
    setAttachedContents(prev => prev.filter(c => (c.content_id || c.id) !== id))
  }

  const loadContent = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getContent(contentId)
      console.log('API Response:', res)
      
      // API returns { success, metadata, content (base64), contentEncoding }
      const contentData = res.metadata || res
      
      // Decode base64 content if present
      if (res.content && res.contentEncoding === 'base64') {
        try {
          // Decode base64 to text
          const decodedText = atob(res.content)
          contentData.content_text = decodedText
          console.log('Decoded content_text length:', decodedText.length)
          console.log('First 500 chars:', decodedText.substring(0, 500))
        } catch (e) {
          console.error('Failed to decode base64 content:', e)
        }
      }
      
      console.log('Final contentData:', contentData)
      setContent(contentData)
    } catch (err) {
      console.error('Load content error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const loadRawContent = async () => {
    if (rawContent || rawLoading) return
    setRawLoading(true)
    try {
      const res = await getRawContent(contentId)
      setRawContent(res)
    } catch (err) {
      console.error('Failed to load raw content:', err)
    } finally {
      setRawLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="p-8 rounded-xl bg-destructive/10 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-primary hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  if (!content) return null

  const config = contentTypeConfig[content.type] || contentTypeConfig.unknown
  const Icon = iconMap[content.type] || File

  // Render content based on type
  const renderContentViewer = () => {
    const type = content.type?.toLowerCase()
    console.log('Content type:', type, 'Full content:', content)

    switch (type) {
      case 'bookmark':
        return <BookmarkViewer content={content} />
      case 'image':
        return <ImageViewer content={content} contentId={contentId} />
      case 'pdf':
        return <PDFViewer content={content} contentId={contentId} />
      case 'epub':
        return <EPUBViewer content={content} contentId={contentId} />
      case 'note':
      case 'txt':
      case 'transcript':
        return <MarkdownViewer content={content} />
      case 'docx':
      case 'doc':
      case 'xlsx':
      case 'xls':
      case 'pptx':
      case 'ppt':
        return <OfficeViewer content={content} contentId={contentId} />
      default:
        // For any content with text, show markdown viewer
        if (content.content_text || content.summary || content.description) {
          return <MarkdownViewer content={content} />
        }
        return <DefaultViewer content={content} />
    }
  }

  // Render sidebar tree
  const renderSidebar = () => (
    <div className="w-72 border-r border-border flex flex-col bg-card/50 h-full">
      <div className="p-3 border-b border-border flex items-center justify-between">
        <span className="font-medium text-sm">Knowledge Base</span>
        <button
          onClick={() => setShowSidebar(false)}
          className="p-1 rounded hover:bg-accent"
        >
          <PanelLeftClose className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-auto p-2">
        {treeLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1">
            {/* Root level contents */}
            {rootContents.length > 0 && (
              <div className="mb-2">
                {rootContents.map(item => {
                  const ItemIcon = iconMap[item.type] || File
                  const itemConfig = contentTypeConfig[item.type] || contentTypeConfig.unknown
                  const isActive = item.content_id === contentId
                  return (
                    <button
                      key={item.content_id}
                      onClick={() => navigate(`/content/${item.content_id}`)}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
                        isActive ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
                      }`}
                    >
                      <ItemIcon className="w-4 h-4 flex-shrink-0" style={{ color: itemConfig.color }} />
                      <span className="truncate">{item.title}</span>
                    </button>
                  )
                })}
              </div>
            )}
            
            {/* Spaces/folders */}
            {spaces.map(space => (
              <div key={space.id}>
                <button
                  onClick={() => toggleSpace(space.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-accent text-left text-sm"
                >
                  {expandedSpaces[space.id] ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  )}
                  {expandedSpaces[space.id] ? (
                    <FolderOpen className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <Folder className="w-4 h-4 text-yellow-500" />
                  )}
                  <span className="truncate">{space.name}</span>
                </button>
                {expandedSpaces[space.id] && spaceContents[space.id] && (
                  <div className="ml-6 space-y-0.5">
                    {spaceContents[space.id].map(item => {
                      const ItemIcon = iconMap[item.type] || File
                      const itemConfig = contentTypeConfig[item.type] || contentTypeConfig.unknown
                      const isActive = item.content_id === contentId
                      return (
                        <button
                          key={item.content_id}
                          onClick={() => navigate(`/content/${item.content_id}`)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-sm transition-colors ${
                            isActive ? 'bg-primary/10 text-primary' : 'hover:bg-accent'
                          }`}
                        >
                          <ItemIcon className="w-4 h-4 flex-shrink-0" style={{ color: itemConfig.color }} />
                          <span className="truncate">{item.title}</span>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="h-screen flex flex-col animate-fade-in">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {layoutMode === 'master-detail' && !showSidebar && (
                <button
                  onClick={() => setShowSidebar(true)}
                  className="p-2 rounded-lg hover:bg-accent transition-colors"
                  title="Show sidebar"
                >
                  <PanelLeftClose className="w-5 h-5 rotate-180" />
                </button>
              )}
              <button
                onClick={() => navigate(-1)}
                className="p-2 rounded-lg hover:bg-accent transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-accent transition-colors"
                title="Home"
              >
                <Home className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2 ml-2">
                <div
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${config.color}20` }}
                >
                  <Icon className="w-4 h-4" style={{ color: config.color }} />
                </div>
                <div className="hidden sm:block">
                  <h1 className="font-semibold truncate max-w-[300px]">{content.title}</h1>
                  <p className="text-xs text-muted-foreground">{config.label}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Layout mode toggle */}
              <div className="flex items-center gap-1 p-1 rounded-lg bg-muted">
                <button
                  onClick={() => {
                    setLayoutMode('single')
                    setShowSidebar(false)
                  }}
                  className={`p-2 rounded-md transition-colors ${layoutMode === 'single' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
                  title="Single Panel"
                >
                  <Square className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setLayoutMode('master-detail')
                    setShowSidebar(true)
                  }}
                  className={`p-2 rounded-md transition-colors ${layoutMode === 'master-detail' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
                  title="Master-Detail"
                >
                  <Columns className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setLayoutMode('focus')
                    setShowSidebar(false)
                  }}
                  className={`p-2 rounded-md transition-colors ${layoutMode === 'focus' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
                  title="Focus Mode"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
              </div>

              {/* View mode toggle */}
              <div className="hidden sm:flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border/50">
                <button
                  onClick={() => setViewMode(ViewMode.ORIGINAL)}
                  className={`p-2 rounded-lg transition-all ${viewMode === ViewMode.ORIGINAL ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  title="Original View"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode(ViewMode.MARKDOWN)}
                  className={`p-2 rounded-lg transition-all ${viewMode === ViewMode.MARKDOWN ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  title="Markdown View"
                >
                  <FileText className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode(ViewMode.LISTEN)}
                  className={`p-2 rounded-lg transition-all ${viewMode === ViewMode.LISTEN ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  title="Listen (Text-to-Speech)"
                >
                  <Volume2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode(ViewMode.INFO)}
                  className={`p-2 rounded-lg transition-all ${viewMode === ViewMode.INFO ? 'bg-background shadow-sm text-primary' : 'text-muted-foreground hover:text-foreground hover:bg-background/50'}`}
                  title="Info"
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>

              {content.state === 'indexed' && (
                <button
                  onClick={openChat}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors shadow-lg ${
                    showChat 
                      ? 'bg-purple-600 text-white shadow-purple-500/25' 
                      : 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700 shadow-purple-500/25'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span className="hidden sm:inline">Chat</span>
                </button>
              )}

              {content.url && content.type === 'bookmark' && (
                <a
                  href={content.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-lg bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors"
                  title="Open URL"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}

              <button className="p-2 rounded-lg hover:bg-accent transition-colors" title="Download">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area with optional sidebar */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar */}
        {layoutMode === 'master-detail' && showSidebar && renderSidebar()}
        
        {/* Content */}
        <div className="flex-1 overflow-auto">
          {viewMode === ViewMode.INFO ? (
            <ContentInfoView content={content} config={config} Icon={Icon} />
          ) : viewMode === ViewMode.MARKDOWN ? (
            <MarkdownViewer content={content} />
          ) : viewMode === ViewMode.LISTEN ? (
            <ListenViewer content={content} contentId={contentId} />
          ) : (
            renderContentViewer()
          )}
        </div>

        {/* Chat Drawer */}
        {showChat && (
          <div className="w-96 border-l border-border flex flex-col bg-card h-full">
            {/* Chat Header */}
            <div className="p-3 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                <span className="font-medium">Chat with AI</span>
              </div>
              <button
                onClick={() => setShowChat(false)}
                className="p-1 rounded hover:bg-accent"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Attached Content */}
            <AttachedContents
              contents={[content, ...attachedContents]}
              onRemove={handleRemoveContent}
              onAdd={() => setShowContentPicker(true)}
            />

            {/* Chat Messages */}
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {chatMessages.length === 0 ? (
                <div className="text-center py-8">
                  <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground text-sm">
                    Ask questions about this content
                  </p>
                  <p className="text-muted-foreground text-xs mt-1">
                    "{content.title}"
                  </p>
                </div>
              ) : (
                chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role === 'assistant' && (
                      <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-purple-500" />
                      </div>
                    )}
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        msg.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {msg.content || '...'}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <p className="text-sm">{msg.content}</p>
                      )}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                  </div>
                ))
              )}
              {chatLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                    <Loader2 className="w-4 h-4 text-purple-500 animate-spin" />
                  </div>
                  <div className="bg-muted rounded-2xl px-4 py-2">
                    <p className="text-sm text-muted-foreground">Thinking...</p>
                  </div>
                </div>
              )}
            </div>

            {/* Chat Input */}
            <div className="p-3 border-t border-border">
              <div className="flex gap-2">
                <button
                  onClick={() => setShowContentPicker(true)}
                  className={`p-2 rounded-xl transition-colors ${
                    attachedContents.length > 0
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-accent text-muted-foreground hover:text-foreground'
                  }`}
                  title="Attach more content"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <input
                  type="text"
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Ask about attached content..."
                  className="flex-1 px-4 py-2 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                  disabled={chatLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!chatInput.trim() || chatLoading}
                  className="p-2 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content Picker Modal */}
        <ContentPickerModal
          isOpen={showContentPicker}
          onClose={() => setShowContentPicker(false)}
          onSelect={handleContentSelect}
          selectedIds={[contentId, ...attachedContents.map(c => c.content_id || c.id)]}
        />
      </div>

      {/* Indexing status indicator */}
      {content.state && content.state !== 'indexed' && (
        <div className="fixed bottom-6 right-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border shadow-lg">
          <Loader2 className="w-4 h-4 animate-spin text-orange-500" />
          <span className="text-sm font-medium text-orange-500">
            {content.state === 'extracting' ? 'Extracting content...' : 'Indexing...'}
          </span>
        </div>
      )}
    </div>
  )
}

// Content Info View - shows metadata and summary
function ContentInfoView({ content, config, Icon }) {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-8">
        <div className="flex items-start gap-4 mb-4">
          <div
            className="p-4 rounded-2xl"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <Icon className="w-8 h-8" style={{ color: config.color }} />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">{content.title}</h1>
            <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span
                className="px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: `${config.color}20`, color: config.color }}
              >
                {config.label}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" />
                {formatDate(content.created_at || content.createdAt)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                Updated {formatDate(content.updated_at || content.updatedAt)}
              </span>
              {content.state && (
                <span className={`flex items-center gap-1 ${content.state === 'indexed' ? 'text-green-500' : 'text-orange-500'}`}>
                  {content.state === 'indexed' ? '✓ Indexed' : `⏳ ${content.state}`}
                </span>
              )}
            </div>
          </div>
        </div>

        {content.description && (
          <p className="text-muted-foreground text-lg leading-relaxed mb-6">
            {content.description}
          </p>
        )}

        {content.url && (
          <a
            href={content.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors"
          >
            <Globe className="w-4 h-4 text-primary" />
            <span className="text-sm truncate max-w-[400px]">{content.url}</span>
            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          </a>
        )}
      </div>

      {content.summary && (
        <div className="mb-8 p-6 rounded-2xl bg-primary/5 border border-primary/20">
          <h2 className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            AI Summary
          </h2>
          <p className="text-foreground leading-relaxed">{content.summary}</p>
        </div>
      )}

      {content.og_image && (
        <div className="rounded-2xl overflow-hidden border border-border">
          <img
            src={content.og_image}
            alt={content.title}
            className="w-full object-cover"
          />
        </div>
      )}
    </div>
  )
}

// Bookmark Viewer - iframe for web pages with fallback to extracted content
function BookmarkViewer({ content }) {
  const [iframeError, setIframeError] = useState(false)
  const [viewMode, setViewMode] = useState('iframe') // 'iframe' | 'content'

  if (!content.url) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <p className="text-muted-foreground">No URL provided</p>
      </div>
    )
  }

  // Get extracted text content
  const textContent = typeof content.content_text === 'string' 
    ? content.content_text 
    : String(content.content_text || '')

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      {/* View mode toggle */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card/50">
        <div className="flex items-center gap-2">
          <a
            href={content.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline truncate max-w-[400px]"
          >
            {content.url}
          </a>
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted">
          <button
            onClick={() => setViewMode('iframe')}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${viewMode === 'iframe' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            Website
          </button>
          <button
            onClick={() => setViewMode('content')}
            className={`px-3 py-1 rounded-md text-sm transition-colors ${viewMode === 'content' ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
          >
            Extracted
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'iframe' ? (
          <iframe
            src={content.url}
            className="w-full h-full border-0"
            title={content.title}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
            onError={() => setIframeError(true)}
          />
        ) : (
          <div className="h-full overflow-auto">
            <div className="max-w-4xl mx-auto px-6 py-8">
              {content.og_image && (
                <img 
                  src={content.og_image} 
                  alt={content.title}
                  className="w-full max-h-64 object-cover rounded-lg mb-6"
                />
              )}
              {content.description && (
                <p className="text-muted-foreground mb-6 text-lg">{content.description}</p>
              )}
              {textContent ? (
                <div className="prose dark:prose-invert max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {textContent}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-muted-foreground">No extracted content available</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Image Viewer - fetches image with authentication
function ImageViewer({ content, contentId }) {
  const [imageUrl, setImageUrl] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadImage()
    return () => {
      // Cleanup blob URL when component unmounts
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl)
      }
    }
  }, [contentId])

  const loadImage = async () => {
    setLoading(true)
    setError(null)

    // First try to use existing URL if available (external URLs)
    const existingUrl = content.url || content.og_image || content.image
    if (existingUrl && existingUrl.startsWith('http') && !existingUrl.includes('api.sigmanote.app')) {
      // External URL, use directly
      setImageUrl(existingUrl)
      setLoading(false)
      return
    }

    // Fetch image with authentication using the file endpoint
    try {
      const token = await getIdToken()
      const API_BASE = import.meta.env.VITE_API_URL || 'https://api.sigmanote.app/api'

      // Try the file endpoint first (for uploaded files)
      const response = await fetch(`${API_BASE}/content/${contentId}/file`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      console.log('Image fetch response:', response.status, response.headers.get('content-type'))

      if (!response.ok) {
        // Fallback to raw endpoint
        console.log('Trying raw endpoint...')
        const rawResponse = await fetch(`${API_BASE}/content/${contentId}?raw=true`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (!rawResponse.ok) {
          throw new Error(`Failed to load image: ${rawResponse.status}`)
        }

        const contentType = rawResponse.headers.get('content-type') || 'image/jpeg'
        console.log('Raw response content-type:', contentType)

        // Check if response is JSON (might contain base64)
        if (contentType.includes('application/json')) {
          const json = await rawResponse.json()
          console.log('JSON response for image:', json)
          if (json.content && json.contentEncoding === 'base64') {
            // It's base64 encoded image
            const mimeType = json.mimeType || 'image/jpeg'
            setImageUrl(`data:${mimeType};base64,${json.content}`)
            return
          }
        }

        const arrayBuffer = await rawResponse.arrayBuffer()
        const blob = new Blob([arrayBuffer], { type: contentType.includes('image') ? contentType : 'image/jpeg' })
        const url = URL.createObjectURL(blob)
        setImageUrl(url)
        return
      }

      // Get content type from response headers
      const contentType = response.headers.get('content-type') || 'image/jpeg'
      console.log('File response content-type:', contentType)

      // Check if it's JSON (might be base64 encoded)
      if (contentType.includes('application/json')) {
        const json = await response.json()
        console.log('JSON response:', json)
        if (json.url) {
          // Signed URL returned
          setImageUrl(json.url)
          return
        }
        if (json.content && json.contentEncoding === 'base64') {
          const mimeType = json.mimeType || 'image/jpeg'
          setImageUrl(`data:${mimeType};base64,${json.content}`)
          return
        }
      }

      const arrayBuffer = await response.arrayBuffer()
      const blob = new Blob([arrayBuffer], { type: contentType.includes('image') ? contentType : 'image/jpeg' })
      const url = URL.createObjectURL(blob)
      setImageUrl(url)
    } catch (err) {
      console.error('Image load error:', err)
      setError(err.message)
      // Fallback to any available URL
      const fallbackUrl = content.url || content.og_image || content.image
      if (fallbackUrl) {
        setImageUrl(fallbackUrl)
        setError(null)
      }
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading image...</p>
        </div>
      </div>
    )
  }

  if (error || !imageUrl) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <ImageIcon className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">{error || 'No image available'}</p>
          {error && (
            <button
              onClick={loadImage}
              className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center p-8 min-h-[calc(100vh-8rem)] bg-black/5 dark:bg-white/5">
      <img
        src={imageUrl}
        alt={content.title}
        className="max-w-full max-h-[80vh] rounded-lg shadow-2xl object-contain"
        onError={() => setError('Failed to display image')}
      />
    </div>
  )
}

// PDF Viewer - uses browser's native PDF viewer with authenticated fetch
function PDFViewer({ content, contentId }) {
  const [pdfUrl, setPdfUrl] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [pdfError, setPdfError] = useState(null)

  useEffect(() => {
    loadPdf()
    return () => {
      // Cleanup blob URL when component unmounts
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl)
      }
    }
  }, [contentId])

  const loadPdf = async () => {
    setPdfLoading(true)
    setPdfError(null)
    try {
      const { getIdToken } = await import('../lib/firebase')
      const token = await getIdToken()
      const API_BASE = import.meta.env.VITE_API_URL || 'https://api.sigmanote.app/api'
      
      const response = await fetch(`${API_BASE}/content/${contentId}?raw=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      
      if (!response.ok) {
        throw new Error(`Failed to load PDF: ${response.status}`)
      }
      
      // Get the raw data and create a blob with explicit PDF mime type
      const arrayBuffer = await response.arrayBuffer()
      const pdfBlob = new Blob([arrayBuffer], { type: 'application/pdf' })
      const url = URL.createObjectURL(pdfBlob)
      setPdfUrl(url)
    } catch (err) {
      console.error('PDF load error:', err)
      setPdfError(err.message)
    } finally {
      setPdfLoading(false)
    }
  }

  if (pdfLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading PDF...</p>
        </div>
      </div>
    )
  }

  if (pdfError) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="p-8 rounded-2xl bg-destructive/10 text-center">
          <FileText className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-destructive mb-4">{pdfError}</p>
          <button
            onClick={loadPdf}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-4rem)]">
      <iframe
        src={pdfUrl}
        className="w-full h-full border-0"
        title={content.title}
      />
    </div>
  )
}

// EPUB Viewer - using react-reader (epub.js wrapper)
function EPUBViewer({ content, contentId }) {
  const [epubData, setEpubData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [location, setLocation] = useState(null)
  const [ReactReader, setReactReader] = useState(null)

  // Dynamically import react-reader to avoid SSR issues
  useEffect(() => {
    import('react-reader').then(module => {
      setReactReader(() => module.ReactReader)
    }).catch(err => {
      console.error('Failed to load react-reader:', err)
      setError('Failed to load EPUB reader')
    })
  }, [])

  useEffect(() => {
    loadEpub()
  }, [contentId])

  const loadEpub = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getIdToken()
      const API_BASE = import.meta.env.VITE_API_URL || 'https://api.sigmanote.app/api'
      console.log('Fetching EPUB from:', `${API_BASE}/content/${contentId}?raw=true`)
      const response = await fetch(`${API_BASE}/content/${contentId}?raw=true`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      console.log('EPUB response status:', response.status)
      if (!response.ok) throw new Error(`Failed to load EPUB: ${response.status}`)
      const arrayBuffer = await response.arrayBuffer()
      console.log('EPUB arrayBuffer size:', arrayBuffer.byteLength)
      // react-reader can accept ArrayBuffer directly
      setEpubData(arrayBuffer)
    } catch (err) {
      console.error('EPUB load error:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (loading || !ReactReader) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading EPUB...</p>
        </div>
      </div>
    )
  }

  if (error || !epubData) {
    // Fallback to text preview if EPUB loading fails
    const textContent = typeof content.content_text === 'string' 
      ? content.content_text 
      : String(content.content_text || '')
    
    return (
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="p-8 rounded-2xl bg-card border border-border">
          <div className="text-center mb-6">
            <BookOpen className="w-12 h-12 text-purple-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold">{content.title || 'EPUB Reader'}</h2>
            {error && <p className="text-destructive mt-2 text-sm">{error}</p>}
          </div>
          {textContent && (
            <div className="text-left">
              <h3 className="font-medium mb-4">Extracted Text:</h3>
              <div className="prose dark:prose-invert max-w-none max-h-[60vh] overflow-auto p-4 bg-muted/30 rounded-lg">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {textContent.length > 10000 ? textContent.slice(0, 10000) + '\n\n...(truncated)' : textContent}
                </ReactMarkdown>
              </div>
            </div>
          )}
          <button
            onClick={loadEpub}
            className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Retry EPUB Viewer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-4rem)] relative" style={{ background: '#fff' }}>
      <ReactReader
        url={epubData}
        location={location}
        locationChanged={(loc) => setLocation(loc)}
        title={content.title}
        showToc={true}
        getRendition={(rendition) => {
          console.log('EPUB rendition:', rendition)
        }}
      />
    </div>
  )
}

// Markdown/Text Viewer
function MarkdownViewer({ content }) {
  const text = content.content_text || content.description || ''

  if (!text) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <p className="text-muted-foreground">No text content available</p>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="prose prose-lg dark:prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {text}
        </ReactMarkdown>
      </div>
    </div>
  )
}

// Text-to-Speech Listen Viewer
function ListenViewer({ content, contentId }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [speechRate, setSpeechRate] = useState(() => {
    const saved = localStorage.getItem('tts_rate')
    return saved ? parseFloat(saved) : 1.0
  })
  const [voices, setVoices] = useState([])
  const [selectedLanguage, setSelectedLanguage] = useState(() => localStorage.getItem('tts_language') || 'en')
  const [selectedVoice, setSelectedVoice] = useState(() => localStorage.getItem('tts_voice') || '')
  const [showSettings, setShowSettings] = useState(false)

  const sentenceContainerRef = useRef(null)
  const utteranceRef = useRef(null)

  // Get text content
  const rawText = content?.content_text || content?.description || ''

  // Clean markdown and split into sentences
  const cleanText = (text) => {
    return text
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/`(.+?)`/g, '$1')
      .replace(/\[(.+?)\]\(.+?\)/g, '$1')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      .replace(/\n{2,}/g, '\n')
      .trim()
  }

  const splitIntoSentences = (text) => {
    const cleaned = cleanText(text)
    const sentences = cleaned.split(/(?<=[.!?。！？；।॥۔؛؟ฯ])\s+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
    return sentences.length > 0 ? sentences : [cleaned]
  }

  const sentences = splitIntoSentences(rawText)

  // Language mappings
  const languageMap = {
    'en': { name: 'English', flag: '🇺🇸' },
    'zh': { name: 'Chinese', flag: '🇨🇳' },
    'es': { name: 'Spanish', flag: '🇪🇸' },
    'fr': { name: 'French', flag: '🇫🇷' },
    'de': { name: 'German', flag: '🇩🇪' },
    'ja': { name: 'Japanese', flag: '🇯🇵' },
    'ko': { name: 'Korean', flag: '🇰🇷' },
    'vi': { name: 'Vietnamese', flag: '🇻🇳' },
  }

  // Load voices
  useEffect(() => {
    const loadVoices = () => {
      const available = window.speechSynthesis?.getVoices() || []
      setVoices(available)
    }
    loadVoices()
    window.speechSynthesis?.addEventListener('voiceschanged', loadVoices)
    return () => {
      window.speechSynthesis?.removeEventListener('voiceschanged', loadVoices)
      window.speechSynthesis?.cancel()
    }
  }, [])

  // Load saved position
  useEffect(() => {
    if (contentId) {
      const saved = localStorage.getItem(`tts_position_${contentId}`)
      if (saved) setCurrentIndex(parseInt(saved, 10) || 0)
    }
  }, [contentId])

  // Save position
  useEffect(() => {
    if (contentId && currentIndex > 0) {
      localStorage.setItem(`tts_position_${contentId}`, currentIndex.toString())
    }
  }, [contentId, currentIndex])

  // Auto-scroll to current sentence
  useEffect(() => {
    if (sentenceContainerRef.current) {
      const el = sentenceContainerRef.current.querySelector(`[data-index="${currentIndex}"]`)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [currentIndex])

  // Speech synthesis
  const speakSentence = (index) => {
    if (!window.speechSynthesis || index >= sentences.length) {
      setIsPlaying(false)
      return
    }

    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(sentences[index])
    utterance.rate = speechRate

    // Find voice
    const filteredVoices = voices.filter(v => v.lang.startsWith(selectedLanguage))
    const voice = selectedVoice
      ? filteredVoices.find(v => v.name === selectedVoice)
      : filteredVoices[0]
    if (voice) utterance.voice = voice

    utterance.onend = () => {
      if (index < sentences.length - 1) {
        setCurrentIndex(index + 1)
        speakSentence(index + 1)
      } else {
        setIsPlaying(false)
      }
    }

    utterance.onerror = () => setIsPlaying(false)
    utteranceRef.current = utterance
    window.speechSynthesis.speak(utterance)
  }

  const togglePlay = () => {
    if (isPlaying) {
      window.speechSynthesis?.cancel()
      setIsPlaying(false)
    } else {
      setIsPlaying(true)
      speakSentence(currentIndex)
    }
  }

  const stop = () => {
    window.speechSynthesis?.cancel()
    setIsPlaying(false)
  }

  const skipPrevious = () => {
    const newIndex = Math.max(0, currentIndex - 1)
    setCurrentIndex(newIndex)
    if (isPlaying) speakSentence(newIndex)
  }

  const skipNext = () => {
    const newIndex = Math.min(sentences.length - 1, currentIndex + 1)
    setCurrentIndex(newIndex)
    if (isPlaying) speakSentence(newIndex)
  }

  const jumpToSentence = (index) => {
    setCurrentIndex(index)
    if (isPlaying) speakSentence(index)
  }

  const handleRateChange = (rate) => {
    setSpeechRate(rate)
    localStorage.setItem('tts_rate', rate.toString())
  }

  const handleLanguageChange = (lang) => {
    setSelectedLanguage(lang)
    localStorage.setItem('tts_language', lang)
    setSelectedVoice('')
    localStorage.removeItem('tts_voice')
  }

  const handleVoiceChange = (voiceName) => {
    setSelectedVoice(voiceName)
    localStorage.setItem('tts_voice', voiceName)
  }

  const filteredVoices = voices.filter(v => v.lang.startsWith(selectedLanguage))
  const wordCount = rawText.split(/\s+/).filter(w => w.length > 0).length

  if (!rawText) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <div className="text-center">
          <Volume2 className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <p className="text-muted-foreground">No text content available for speech</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border/50 bg-card/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${isPlaying ? 'bg-primary/20 animate-pulse' : 'bg-primary/10'}`}>
              <Volume2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-semibold line-clamp-1">{content?.title}</h2>
              <p className="text-xs text-muted-foreground">{sentences.length} sentences · {wordCount} words</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setCurrentIndex(0); if (isPlaying) speakSentence(0) }}
              className="p-2 rounded-lg hover:bg-accent transition-colors"
              title="Reset to beginning"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setShowSettings(!showSettings)}
              className={`p-2 rounded-lg transition-colors ${showSettings ? 'bg-primary/10 text-primary' : 'hover:bg-accent'}`}
              title="Settings"
            >
              <Info className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="mt-4 p-4 rounded-xl bg-muted/50 border border-border/50 space-y-4">
            {/* Speed */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Speed: {speechRate}x</label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={speechRate}
                onChange={(e) => handleRateChange(parseFloat(e.target.value))}
                className="w-full accent-primary"
              />
            </div>
            {/* Language */}
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-2 block">Language</label>
              <select
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm"
              >
                {Object.entries(languageMap).map(([code, { name, flag }]) => (
                  <option key={code} value={code}>{flag} {name}</option>
                ))}
              </select>
            </div>
            {/* Voice */}
            {filteredVoices.length > 0 && (
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-2 block">Voice ({filteredVoices.length} available)</label>
                <select
                  value={selectedVoice}
                  onChange={(e) => handleVoiceChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm"
                >
                  <option value="">Default</option>
                  {filteredVoices.map(v => (
                    <option key={v.name} value={v.name}>{v.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Playback controls - at the top */}
      <div className="px-4 py-3 border-b border-border/50 bg-card/50">
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={skipPrevious}
            disabled={currentIndex === 0}
            className="p-2.5 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Previous sentence"
          >
            <ChevronRight className="w-5 h-5 rotate-180" />
          </button>

          <button
            onClick={togglePlay}
            className="p-3.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/25 transition-all active:scale-95"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" fill="currentColor" />
            ) : (
              <Play className="w-6 h-6" fill="currentColor" />
            )}
          </button>

          <button
            onClick={skipNext}
            disabled={currentIndex === sentences.length - 1}
            className="p-2.5 rounded-full hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Next sentence"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {isPlaying && (
            <button
              onClick={stop}
              className="p-2.5 rounded-full hover:bg-destructive/10 text-destructive transition-colors ml-2"
              title="Stop"
            >
              <X className="w-5 h-5" />
            </button>
          )}

          {/* Progress indicator */}
          <div className="ml-4 text-xs text-muted-foreground">
            {currentIndex + 1} / {sentences.length}
          </div>
        </div>
      </div>

      {/* Sentences */}
      <div ref={sentenceContainerRef} className="flex-1 overflow-auto p-6 space-y-3">
        {sentences.map((sentence, index) => (
          <div
            key={index}
            data-index={index}
            onClick={() => jumpToSentence(index)}
            className={`p-4 rounded-xl border cursor-pointer transition-all ${
              index === currentIndex
                ? 'bg-primary/10 border-primary/30 shadow-sm'
                : index < currentIndex
                ? 'bg-muted/30 border-border/50 opacity-60'
                : 'bg-card border-border hover:border-primary/20 hover:bg-accent/30'
            }`}
          >
            <p className={`text-sm leading-relaxed ${index === currentIndex ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              {sentence}
            </p>
          </div>
        ))}
      </div>
    </div>
  )
}

// Office Document Viewer - using Google Docs viewer
function OfficeViewer({ content, contentId }) {
  const API_BASE = import.meta.env.VITE_API_URL || 'https://api.sigmanote.app/api'
  const fileUrl = `${API_BASE}/content/${contentId}?raw=true`

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      <div className="flex-1 bg-muted/30">
        <iframe
          src={`https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`}
          className="w-full h-full border-0"
          title={content.title}
        />
      </div>
      <div className="p-4 bg-card border-t border-border">
        <p className="text-sm text-muted-foreground text-center">
          If the document doesn't load, try{' '}
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            downloading it
          </a>
        </p>
      </div>
    </div>
  )
}

// Default Viewer - fallback for unknown types
function DefaultViewer({ content }) {
  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="p-8 rounded-2xl bg-card border border-border">
        {content.summary && (
          <div className="mb-8 p-6 rounded-xl bg-primary/5 border border-primary/20">
            <h2 className="text-sm font-medium text-primary mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              AI Summary
            </h2>
            <p className="text-foreground leading-relaxed">{content.summary}</p>
          </div>
        )}

        {content.content_text ? (
          <div className="prose prose-lg dark:prose-invert max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {content.content_text}
            </ReactMarkdown>
          </div>
        ) : content.description ? (
          <p className="text-muted-foreground">{content.description}</p>
        ) : (
          <p className="text-muted-foreground text-center">
            No preview available for this content type.
          </p>
        )}

        {content.og_image && (
          <div className="mt-8 rounded-xl overflow-hidden border border-border">
            <img
              src={content.og_image}
              alt={content.title}
              className="w-full object-cover"
            />
          </div>
        )}
      </div>
    </div>
  )
}
